import type { Operation } from '@apollo/client/core';
import config from '@/config';
import unleash from '@/unleash';
import { bodyEncoder } from './body-encoder';

interface CachedSignature {
  signature: string;
  timestamp: number;
}
const SIGNATURE_TTL = 55 * 60; // 55 minutes in seconds

function getCacheKey(operationName: string): string {
  return `signature:${operationName}`;
}

export function getCachedSignature(
  operationName: string,
): CachedSignature | null {
  const cacheKey = getCacheKey(operationName);
  let cachedSignature: string | null = null;

  try {
    cachedSignature = localStorage.getItem(cacheKey);
  } catch {
    cachedSignature = null;
  }

  if (cachedSignature) {
    return JSON.parse(cachedSignature);
  }
  return null;
}

export function setCachedSignature(
  operationName: string,
  signature: string,
  timestamp: number,
) {
  const cacheKey = getCacheKey(operationName);
  const cachedSignature = { signature, timestamp };
  try {
    localStorage.setItem(cacheKey, JSON.stringify(cachedSignature));
  } catch {
    // NOOP if localStorage is not available
  }
}

export async function generateRequestSignature(
  operation: Operation,
  csrfToken: string,
): Promise<{ signature: string; timestamp: number }> {
  if (!unleash.isEnabled('signatureVerificationFrontend')) {
    return { signature: '', timestamp: 0 };
  }

  const cachedSignature = getCachedSignature(operation.operationName);
  const currentTimestamp = Math.floor(Date.now() / 1000);

  if (
    cachedSignature &&
    cachedSignature.timestamp > currentTimestamp - SIGNATURE_TTL
  ) {
    return cachedSignature;
  }

  try {
    const encodedBody = bodyEncoder.encodeBody(
      operation.operationName,
      currentTimestamp,
      operation.variables,
    );
    const response = await fetch(`${config.backendUrl}/api/signature`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify({
        ed: encodedBody,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate signature: ${response.status}`);
    }

    const { signature, timestamp } = await response.json();
    setCachedSignature(operation.operationName, signature, currentTimestamp);

    return { signature, timestamp };
  } catch {
    throw new Error('Failed to generate request signature');
  }
}
